import React, { useState, useEffect, useRef } from 'react';
import ArticleAnchorMenu from 'components/article-anchor-menu';
import PersonTeaserBlock from 'components/person-teaser-block';
import Authors from 'components/authors';
import useBreakpoints from 'hooks/use-breakpoints';
import useDidUpdateEffect from 'hooks/use-did-update-effect';
import { ARTICLE_SCROLL_ANIMATION_DURATION } from '../../constants/animations';
import PropTypes from 'prop-types';
import cn from 'classnames';

const VISIBILITY_OVERLAP_OFFSET = 70;
const OVERLAPPING_BLOCKS_SELECTOR =
  '.story-block, .quote-with-background-block, .image-block, .board-block, .references-block, .rich-text__table-wrapper, .sas-html-injection-block';

const ArticleStickySidebar = ({ anchorMenuHeading, authors }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isOverlapping, setIsOverlapping] = useState(true);
  const [isScrollingToAnchor, setIsScrollingToAnchor] = useState(false);
  const { isMobileArticleSidebar } = useBreakpoints(true);
  const sidebarRef = useRef(null);
  const anchorMenuRef = useRef(null);
  const sidebarStaticPosition = useRef(0);
  const sidebarCurrentHeight = useRef(0);

  const isScrollExceedsSidebarPostion = () => {
    return window.scrollY > sidebarStaticPosition.current;
  };

  const updateIsOverlap = () => {
    if (sidebarRef.current) {
      const sidebarContent = sidebarRef.current.querySelector(
        '.article-sticky-sidebar__content'
      );

      if (!sidebarContent) {
        return;
      }

      const sidebarContentRect = sidebarContent.getBoundingClientRect();
      const overlappingBlocks = document.querySelectorAll(
        OVERLAPPING_BLOCKS_SELECTOR
      );

      let overlapDetected = false;
      overlappingBlocks.forEach(block => {
        const blockRect = block.getBoundingClientRect();

        if (
          blockRect.bottom + VISIBILITY_OVERLAP_OFFSET >
            sidebarContentRect.top &&
          blockRect.top - VISIBILITY_OVERLAP_OFFSET <
            sidebarContentRect.bottom &&
          blockRect.right + VISIBILITY_OVERLAP_OFFSET >
            sidebarContentRect.left &&
          blockRect.left - VISIBILITY_OVERLAP_OFFSET < sidebarContentRect.right
        ) {
          overlapDetected = true;
        }
      });

      setIsOverlapping(overlapDetected);
    }
  };

  const calcSidebarCurrentHeight = () => {
    if (sidebarRef && sidebarRef.current) {
      const sidebarStyles = window.getComputedStyle(sidebarRef.current);

      const marginTop = parseFloat(sidebarStyles.marginTop);
      const marginBottom = parseFloat(sidebarStyles.marginBottom);
      sidebarCurrentHeight.current =
        sidebarRef.current.offsetHeight + marginTop + marginBottom;
    }
  };

  const setIsScrollingToAnchorOnPageLoad = () => {
    const hash = window.location.hash.slice(1);

    if (hash) {
      setIsScrollingToAnchor(true);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      calcSidebarCurrentHeight();
      setIsSticky(isScrollExceedsSidebarPostion());
      updateIsOverlap();
    };

    const handlePageLoad = () => {
      updateIsOverlap();
    };

    setIsScrollingToAnchorOnPageLoad();
    setIsVisible(true);

    window.addEventListener('load', handlePageLoad);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('load', handlePageLoad);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (
      anchorMenuRef &&
      anchorMenuRef.current &&
      !isMobileArticleSidebar &&
      isSticky
    ) {
      setTimeout(
        anchorMenuRef.current.closeAnchorMenu,
        ARTICLE_SCROLL_ANIMATION_DURATION
      );
    }
  }, [isSticky, anchorMenuRef, isMobileArticleSidebar]);

  useEffect(() => {
    const pageContent = document.querySelector('.page__content');
    if (sidebarCurrentHeight.current && sidebarCurrentHeight && pageContent) {
      pageContent.style.paddingTop =
        isSticky && !isMobileArticleSidebar
          ? sidebarCurrentHeight.current + 'px'
          : 0;
    }
  }, [isSticky, sidebarRef, isMobileArticleSidebar]);

  useDidUpdateEffect(() => {
    if (sidebarRef.current) {
      sidebarRef.current.style.position = isMobileArticleSidebar
        ? 'absolute'
        : 'static';

      sidebarStaticPosition.current =
        sidebarRef.current.getBoundingClientRect().top + window.scrollY;
      setIsSticky(isScrollExceedsSidebarPostion());
      sidebarRef.current.removeAttribute('style');
    }
  }, [isMobileArticleSidebar, sidebarRef]);

  const onScrollToAnchorStarted = () => {
    setIsScrollingToAnchor(true);

    setTimeout(() => {
      setIsScrollingToAnchor(false);
    }, ARTICLE_SCROLL_ANIMATION_DURATION);
  };

  return (
    <div
      className={cn('article-sticky-sidebar', {
        'article-sticky-sidebar_visible':
          isVisible && !isOverlapping && !isScrollingToAnchor,
        'article-sticky-sidebar_sticky': isSticky,
      })}
      ref={sidebarRef}
    >
      <div className="article-sticky-sidebar__container">
        <div className="article-sticky-sidebar__content">
          <div className="article-sticky-sidebar__content-item">
            <ArticleAnchorMenu
              heading={anchorMenuHeading}
              ref={anchorMenuRef}
              extraOffsetDesktop={40}
              extraOffsetMobile={isSticky ? 80 : 50}
              onScrollToAnchorStarted={onScrollToAnchorStarted}
            />
          </div>
          {authors && isMobileArticleSidebar && (
            <div className="article-sticky-sidebar__content-item article-sticky-sidebar__content-item_authors">
              <Authors
                items={authors}
                className="article-sticky-sidebar__authors authors_in-article-sidebar"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ArticleStickySidebar.viewModelMeta = 'ignore';

ArticleStickySidebar.propTypes = {
  anchorMenuHeading: PropTypes.string.isRequired,
  authors: PropTypes.arrayOf(PropTypes.exact(PersonTeaserBlock.propTypes)),
};

export default ArticleStickySidebar;
